import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { campaignsEndpoints as endpoints } from '@/api/endpoints';
import { API } from '@/modules/campaigns/api';
import { isEphemeralCampaignId } from '@/modules/campaigns/constants';
import { getPermissions } from '@/redux/permissions/selectors';
import { CampaignAnalyticsPayload } from '@/shared/types/campaigns';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';

/** Matches the endpoint's own 5-minute cache — refetching sooner returns the same value. */
const ANALYTICS_STALE_TIME = 5 * 60 * 1000;

export const useCampaignAnalytics = (campaignId?: string) => {
  const canRead = useSelector((state: RootState) =>
    getPermissions(state, 'campaigns', actions.READ)
  );

  const isSentinel = isEphemeralCampaignId(campaignId);

  const {
    data,
    status: rawStatus,
    error,
    refetch,
  } = useQuery<CampaignAnalyticsPayload, Error>({
    queryKey: [endpoints.campaignAnalytics(campaignId)],
    queryFn: () => API.getCampaignAnalytics(campaignId),
    enabled: !!campaignId && !isSentinel && canRead,
    staleTime: ANALYTICS_STALE_TIME,
  });

  // A disabled query stays 'pending' forever, which would trap the skeleton. Mirror
  // useCampaigns and report the sentinel as settled.
  const status = isSentinel ? 'error' : rawStatus;

  return {
    analytics: data,
    isLoading: status === 'pending',
    isError: status === 'error',
    error,
    refetch,
  };
};
